import { atomWithReset } from 'jotai/utils';

export type SupportedLanguage = 'pt-BR' | 'en-US' | 'es-ES';
export type SupportedDevice = 'desktop' | 'mobile';
export type VariantKey = `${SupportedLanguage}-${SupportedDevice}`;

export const ALL_LANGUAGES: SupportedLanguage[] = ['pt-BR', 'en-US', 'es-ES'];
export const ALL_DEVICES: SupportedDevice[] = ['desktop', 'mobile'];
export const ALL_VARIANT_KEYS: VariantKey[] = ALL_LANGUAGES.flatMap(lang =>
  ALL_DEVICES.map((dev): VariantKey => `${lang}-${dev}`),
);

export interface CanvasVariant {
  canvas: Record<string, unknown> | null;
  backgroundColor: string;
  backgroundImage?: string;
  /**
   * URL do vídeo de fundo (campo `videoSrc` no JSON do canvas). Exclusivo com
   * `backgroundImage` — quando presente, é o fundo renderizado da variante.
   */
  backgroundVideo?: string;
  thumbnail?: string;
}

/** Período de exibição do slide. Ambos os campos são datas no formato YYYY-MM-DD. */
export interface DisplayPeriod {
  startDate?: string;
  endDate?: string;
}

export interface SlideData {
  id: number;
  name: string;
  variants: Record<VariantKey, CanvasVariant>;
  /** Período em que o slide deve ser exibido. Ausente = exibição permanente. */
  displayPeriod?: DisplayPeriod;
}

export const createDefaultVariants = (): Record<VariantKey, CanvasVariant> =>
  Object.fromEntries(
    ALL_VARIANT_KEYS.map(key => [key, { canvas: null, backgroundColor: '#141414' }]),
  ) as Record<VariantKey, CanvasVariant>;

export const createDefaultSlide = (name: string): SlideData => ({
  id: Math.random(),
  name,
  variants: createDefaultVariants(),
});

/**
 * Estado do slide em edição.
 *
 * O editor sempre trabalha sobre **um único** slide por vez (aberto a partir da
 * biblioteca via `slideEditorParamsAtom`), por isso o átomo guarda diretamente o
 * `SlideData` — sem lista nem índice de paginação.
 */
export const slideAtom = atomWithReset<SlideData>(createDefaultSlide('Slide 1'));

slideAtom.debugLabel = 'slide-editor/slideAtom';
